// Reference-counted scroll lock for tour overlays.
//
// The app's actual scroll container is `#root` (see src/styles.css), not the
// document. Locking only body/html leaves #root scrollable, so we must lock
// #root explicitly. We also attach non-passive touchmove/wheel guards so iOS
// WKWebView drag and desktop wheel cannot scroll the container either.

let lockCount = 0;
let prev: {
  bodyOverflow: string;
  bodyTouchAction: string;
  bodyOverscroll: string;
  htmlOverflow: string;
  rootOverflow: string;
  rootTouchAction: string;
  rootOverscroll: string;
} | null = null;

function getRoot(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  return document.getElementById("root");
}

// Allow scrolling inside elements that opt in (e.g. the GPS sheet target,
// or the caption itself). Anything tagged data-tour-scroll="allow" is
// permitted; everything else is silently blocked while locked.
function isAllowedScrollTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Node)) return false;
  let el: HTMLElement | null = target instanceof HTMLElement
    ? target
    : (target.parentElement as HTMLElement | null);
  while (el) {
    if (el.dataset && el.dataset.tourScroll === "allow") return true;
    el = el.parentElement;
  }
  return false;
}

function preventIfBlocked(e: Event) {
  if (isAllowedScrollTarget(e.target)) return;
  // Must be cancelable to actually stop the scroll.
  if (e.cancelable) e.preventDefault();
}

export function lockScroll() {
  if (typeof document === "undefined") return;
  lockCount++;
  if (lockCount > 1) return;
  const body = document.body;
  const html = document.documentElement;
  const root = getRoot();
  prev = {
    bodyOverflow: body.style.overflow,
    bodyTouchAction: body.style.touchAction,
    bodyOverscroll: body.style.overscrollBehavior,
    htmlOverflow: html.style.overflow,
    rootOverflow: root?.style.overflow ?? "",
    rootTouchAction: root?.style.touchAction ?? "",
    rootOverscroll: root?.style.overscrollBehavior ?? "",
  };
  body.style.overflow = "hidden";
  body.style.touchAction = "none";
  body.style.overscrollBehavior = "none";
  html.style.overflow = "hidden";
  if (root) {
    root.style.overflow = "hidden";
    root.style.touchAction = "none";
    root.style.overscrollBehavior = "none";
  }
  // Belt and suspenders: kill scroll gestures at the window level.
  window.addEventListener("touchmove", preventIfBlocked, { passive: false, capture: true });
  window.addEventListener("wheel", preventIfBlocked, { passive: false, capture: true });
}

export function unlockScroll() {
  if (typeof document === "undefined") return;
  if (lockCount === 0) return;
  lockCount--;
  if (lockCount > 0) return;
  const body = document.body;
  const html = document.documentElement;
  const root = getRoot();
  if (prev) {
    body.style.overflow = prev.bodyOverflow;
    body.style.touchAction = prev.bodyTouchAction;
    body.style.overscrollBehavior = prev.bodyOverscroll;
    html.style.overflow = prev.htmlOverflow;
    if (root) {
      root.style.overflow = prev.rootOverflow;
      root.style.touchAction = prev.rootTouchAction;
      root.style.overscrollBehavior = prev.rootOverscroll;
    }
  }
  prev = null;
  window.removeEventListener("touchmove", preventIfBlocked, { capture: true } as any);
  window.removeEventListener("wheel", preventIfBlocked, { capture: true } as any);
}

/** Briefly release the lock so a programmatic scroll on #root can take effect, then re-lock. */
export function withTemporaryUnlock(fn: () => void) {
  if (typeof document === "undefined") { fn(); return; }
  const wasLocked = lockCount > 0;
  const root = getRoot();
  if (wasLocked) {
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
    document.body.style.touchAction = "";
    if (root) {
      root.style.overflow = "auto";
      root.style.touchAction = "";
    }
  }
  try {
    fn();
  } finally {
    if (wasLocked) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      document.body.style.touchAction = "none";
      if (root) {
        root.style.overflow = "hidden";
        root.style.touchAction = "none";
      }
    }
  }
}
